import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Package, User, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { adminAPI } from '../../services/apiService';
import { toast } from 'sonner';

const TodayOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    byStatus: { pending: 0, accepted: 0, completed: 0, rejected: 0 }
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
      return;
    }

    loadTodayOrders();
  }, [navigate]);

  const loadTodayOrders = async () => {
    try {
      const data = await adminAPI.getTodayOrdersDetails();
      setOrders(data.orders || []);
      setStats({
        totalOrders: data.totalOrders || 0,
        byStatus: data.byStatus || { pending: 0, accepted: 0, completed: 0, rejected: 0 }
      });
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading today orders:', error);
      toast.error('Failed to load today\'s orders');
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { className: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      'Accepted': { className: 'bg-blue-100 text-blue-800', text: 'Accepted' },
      'Completed': { className: 'bg-green-100 text-green-800', text: 'Completed' },
      'Rejected': { className: 'bg-red-100 text-red-800', text: 'Rejected' },
      'In Progress': { className: 'bg-purple-100 text-purple-800', text: 'In Progress' }
    };
    const config = statusConfig[status] || statusConfig['Pending'];
    return <Badge className={config.className}>{config.text}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading today's orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <Button 
            onClick={() => navigate('/admin/dashboard')}
            variant="outline"
            className="border-2 border-red-400 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Today's Orders</h1>
          <p className="text-gray-700 text-lg font-medium">Orders placed today with complete details</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <Card className="border-3 border-blue-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Total Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">{stats.totalOrders}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-yellow-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-yellow-600">{stats.byStatus.pending}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-blue-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Accepted</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">{stats.byStatus.accepted}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-green-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">{stats.byStatus.completed}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-red-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Rejected</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">{stats.byStatus.rejected}</p>
            </CardContent>
          </Card>
        </div>

        {/* Orders List */}
        <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Order Details</CardTitle>
          </CardHeader>
          <CardContent>
            {orders && orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map((order) => (
                  <Card key={order._id || order.id} className="border-2 hover:border-red-400 transition-colors">
                    <CardContent className="p-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Left Column - User & Customer Info */}
                        <div className="space-y-4">
                          <div className="border-b pb-3">
                            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                              <User className="h-5 w-5 text-blue-600" />
                              Referrer Information
                            </h3>
                            <p><strong>Name:</strong> {order.userName}</p>
                            <p className="text-sm text-gray-600">{order.userEmail}</p>
                          </div>

                          <div>
                            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                              <User className="h-5 w-5 text-green-600" />
                              Customer Information
                            </h3>
                            <p><strong>Name:</strong> {order.customerName}</p>
                            <p className="flex items-center gap-2 text-sm mt-1">
                              <Mail className="h-4 w-4" />
                              {order.customerEmail}
                            </p>
                            <p className="flex items-center gap-2 text-sm mt-1">
                              <Phone className="h-4 w-4" />
                              {order.mobileNumber}
                            </p>
                            <p className="flex items-start gap-2 text-sm mt-2">
                              <MapPin className="h-4 w-4 mt-1" />
                              {order.fulladdress}
                            </p>
                          </div>
                        </div>

                        {/* Right Column - Product & Status Info */}
                        <div className="space-y-4">
                          <div className="border-b pb-3">
                            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                              <Package className="h-5 w-5 text-purple-600" />
                              Product Details
                            </h3>
                            <p><strong>Product:</strong> {order.productType}</p>
                            {order.speedRequired && (
                              <p><strong>Speed:</strong> {order.speedRequired}</p>
                            )}
                          </div>

                          <div>
                            <h3 className="font-bold text-lg mb-2">Order Status</h3>
                            <div className="mb-2">{getStatusBadge(order.status)}</div>
                            <p className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="h-4 w-4" />
                              {new Date(order.createdAt).toLocaleString('en-IN')}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Order ID: {order._id || order.id}
                            </p>
                            {order.notes && (
                              <div className="mt-3 p-3 bg-gray-50 rounded">
                                <p className="text-sm"><strong>Admin Notes:</strong></p>
                                <p className="text-sm text-gray-700">{order.notes}</p>
                              </div>
                            )}
                          </div>

                          <Button 
                            onClick={() => navigate(`/admin/orders`)}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Manage This Order
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No orders placed today</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TodayOrders;
